"use client";

import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import {
  type CSSProperties,
  type ComponentProps,
  type ElementType,
  memo,
  useMemo,
} from "react";

export interface TextShimmerProps {
  children: string;
  as?: ElementType;
  className?: string;
  duration?: number;
  spread?: number;
}

const ShimmerComponent = ({
  children,
  as: Component = "p",
  className,
  duration = 2,
  spread = 2,
}: TextShimmerProps) => {
  const dynamicSpread = useMemo(
    () => children.length * spread,
    [children, spread],
  );

  const props: ComponentProps<typeof motion.div> = {
    animate: { backgroundPosition: "0% center" },
    className: cn(
      "relative inline-block bg-size-[250%_100%,auto] bg-clip-text text-transparent",
      "[background-repeat:no-repeat,padding-box] [--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--color-background),#0000_calc(50%+var(--spread)))]",
      className,
    ),
    initial: { backgroundPosition: "100% center" },
    style: {
      "--spread": `${dynamicSpread.toString()}px`,
      backgroundImage:
        "var(--bg), linear-gradient(var(--color-muted-foreground), var(--color-muted-foreground))",
    } as CSSProperties,
    transition: {
      repeat: Number.POSITIVE_INFINITY,
      duration,
      ease: "linear" as const,
    },
  };

  if (Component === "p") {
    return <motion.p {...props}>{children}</motion.p>;
  }
  if (Component === "span") {
    return <motion.span {...props}>{children}</motion.span>;
  }
  if (Component === "div") {
    return <motion.div {...props}>{children}</motion.div>;
  }
  if (Component === "h1") {
    return <motion.h1 {...props}>{children}</motion.h1>;
  }
  if (Component === "h2") {
    return <motion.h2 {...props}>{children}</motion.h2>;
  }
  if (Component === "h3") {
    return <motion.h3 {...props}>{children}</motion.h3>;
  }

  return <motion.p {...props}>{children}</motion.p>;
};

export const Shimmer = memo(ShimmerComponent);
